<template>
  <div>
    <AuBreadcrumb :pages="breadcrumb" />

    <ProjectFormFirstStep
      v-if="currentStep == 1"
      :id="id"
      :first-part-of-project-info="firstPartOfProject"
      @nextStep="currentStep++"
      @firstPartOfProject="addFirstPartOfProject"
    />
    <ProjectFormSecondStep
      v-if="currentStep == 2"
      :id="id"
      :show-price-redeem="showPriceRedeem"
      :tokens-count="tokensCount"
      :first-part-of-project-info="firstPartOfProject"
      :second-part-of-project-info="secondPartOfProject"
      @second-part-of-project-info="addSecondPartOfProject"
      @prevStep="currentStep--"
      @nextStep="currentStep++"
    />
    <ProjectFormThirdStep
      v-if="currentStep == 3"
      :id="id"
      :loading="loading"
      :third-part-of-project-info="thirdPartOfProject"
      :error="error"
      @third-part-of-project-info="addThirdPartOfProject"
      @prevStep="
        error = null;
        currentStep--;
      "
      @send="submitProject"
    />
  </div>
</template>

<script>
import _ from "lodash";

import ProjectFormFirstStep from "@/components/issuerForm/ProjectFormFirstStepV2.vue";
import ProjectFormSecondStep from "@/components/issuerForm/ProjectFormSecondStepV2.vue";
import ProjectFormThirdStep from "@/components/issuerForm/ProjectFormThirdStep.vue";
import { checkAccess, scope } from "@/help/accessData";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    ProjectFormThirdStep,
    ProjectFormFirstStep,
    ProjectFormSecondStep
  },

  props: {
    id: {
      type: String,
      default: "new"
    }
  },

  data() {
    return {
      breadcrumb: [
        { key: 0, name: this.$t("router.pages.dashboard"), path: "/" },
        { key: 1, name: this.$t("router.pages.projects"), path: "/my-projects" }
      ],
      currentStep: 1,
      firstPartOfProject: null,
      secondPartOfProject: null,
      thirdPartOfProject: null,
      index: null,
      loading: false,
      error: null
    };
  },

  computed: {
    ...mapGetters({
      tokens: "tokens",
      ethAddresses: "ethAddresses",
      requestsToIssue: "requestsToIssue"
    }),
    tokensCount() {
      return this.firstPartOfProject
        ? this.firstPartOfProject.tokensCount
        : "0";
    },
    showPriceRedeem() {
      return this.firstPartOfProject.investType == "redeemable";
    },
    isRwa() {
      return !checkAccess(scope.notRwa);
    }
  },

  async mounted() {
    if (_.isEmpty(this.requestsToIssue)) {
      await this.getHydraRequestToIssue();
    }
    if (this.id !== "new") {
      this.requestsToIssue.find((item, i) => {
        if (item.id == this.id) {
          this.index = i;
          return true;
        }
      });
    }
  },

  methods: {
    ...mapActions({
      getHydraRequestToIssue: "getHydraRequestToIssue",
      setRequestToIssue: "setRequestToIssue",
      putRequestToIssue: "putRequestToIssue",
      editRequestToIssue: "editRequestToIssue",
      uploadFile: "files/uploadFile",
      patchFile: "files/patchFile"
    }),
    addFirstPartOfProject(value) {
      this.firstPartOfProject = value;
    },
    addSecondPartOfProject(value) {
      this.secondPartOfProject = value;
    },
    addThirdPartOfProject(value) {
      this.thirdPartOfProject = value;
    },

    async doAction() {
      if (this.id === "new") {
        await this.sendForm();
      }
      else {
        await this.editForm();
      }
    },

    async submitProject() {
      this.loading = true;
      this.error = null;

      try {
        // 1. Формируем базовые данные проекта (общие для create и edit)
        const projectData = {
          user: `/api/users/${this.$auth.user().id}`,
          ethAddress: !_.isEmpty(this.ethAddresses)
            ? `/api/eth_addresses/${this.ethAddresses[0].id}`
            : "/api/eth_addresses/1",
          logicType: "asset",
          isForCapitall: true,
          descriptionToken: "-",

          // Данные с первого шага
          ...this.firstPartOfProject && {
            realEstateType: this.firstPartOfProject.realEstateType,
            country: this.firstPartOfProject.country,
            district: this.firstPartOfProject.district,
            city: this.firstPartOfProject.city,
            street: this.firstPartOfProject.street,
            developer: this.firstPartOfProject.developer,
            tourUrl: this.firstPartOfProject.tourUrl,
            totalAreaMt: this.firstPartOfProject.totalAreaMt,
            totalAreaFt: this.firstPartOfProject.totalAreaFt,
            tokenNameEng: this.firstPartOfProject.projectNameEng,
            address: this.firstPartOfProject.address,
            tokenMnemonicEng: this.firstPartOfProject.projectAbbrNameEng,
            shortDescription: this.firstPartOfProject.shortDescription,
            descriptionCapitalRus: this.firstPartOfProject.projectDescriptionRus,
            descriptionCapitalEng: this.firstPartOfProject.projectDescriptionEng,
            tokenCount: this.firstPartOfProject.tokensCount.toString(),
            company: this.firstPartOfProject.company,
            externalPlatform: this.firstPartOfProject.externalPlatform,
            applicationType: this.firstPartOfProject.applicationType,
            hasOpenSale: this.firstPartOfProject.hasOpenSale,
            requiredUserStatus: this.firstPartOfProject?.requiredUserStatus,
            risk: this.firstPartOfProject.risk,
            projectType: this.firstPartOfProject.projectType,
            cryptoActiveType: this.firstPartOfProject.cryptoActiveType,
            burnUnsoldTokens: this.firstPartOfProject.burnUnsoldTokens,
            isDraft: !this.firstPartOfProject.autodeployTokens,
            autoProjectCreate: this.firstPartOfProject.autodeployTokens,
            visibility: this.firstPartOfProject.makeVisible,
            hasEscrow: this.firstPartOfProject.hasEscrow,
            investType: this.firstPartOfProject.investType,
            isVoiting: this.firstPartOfProject.isVoiting
          },

          // Данные со второго шага
          ...this.secondPartOfProject && {
            firstPayout: this.secondPartOfProject.firstPayout,
            dateStart: this.formatDate(this.secondPartOfProject.dateStart),
            dateEnd: this.formatDate(this.secondPartOfProject.dateEnd),
            minCount: this.secondPartOfProject.minCount,
            section: this.secondPartOfProject.section,
            assetType: this.secondPartOfProject.assetType,
            priceBase: this.secondPartOfProject.priceBase,
            tokenQuote: this.secondPartOfProject.tokenQuote,
            feePercent: this.secondPartOfProject.feePercent,
            lockUpTerm: Number(this.secondPartOfProject.lockUpTerm),
            withdrawalDelayTime: Number(this.secondPartOfProject.withdrawalDelayTime),
            bonusMultiplier: this.secondPartOfProject.bonusMultiplier,
            quote: this.secondPartOfProject.quote,
            expectedProfit: this.secondPartOfProject.expectedProfit,
            minCap: isNaN(this.secondPartOfProject?.minCap) ? null : this.secondPartOfProject?.minCap,
            maxCap: this.secondPartOfProject.maxCap,
            terms: this.secondPartOfProject.terms,
            ...this.secondPartOfProject.priceRedeem && {
              priceRedeem: this.secondPartOfProject.priceRedeem,
              dateClose: this.secondPartOfProject.dateClose
            }
          },

          // Данные с третьего шага
          ...this.isRwa && this.thirdPartOfProject?.capitalRisks && {
            risks: this.thirdPartOfProject.capitalRisks
          }
        };

        // 2. Загружаем файлы
        const uploadedFiles = await this.uploadAllFiles();

        // 3. Определяем, создаем или редактируем
        let response;
        let successMessage;

        if (this.id === "new") {
          // СОЗДАНИЕ: сначала создаем проект, потом добавляем файлы
          response = await this.setRequestToIssue(projectData);

          // Проверяем на ошибки
          if (response?.code === 267) {
            this.error = response?.message;
            return;
          }

          // Добавляем файлы к созданному проекту
          if (Object.keys(uploadedFiles).length > 0) {
            await this.putRequestToIssue({
              id: response.id,
              requestToIssue: uploadedFiles
            });
          }

          successMessage = "toasts.success.emissionSend";
        }
        else {
          // РЕДАКТИРОВАНИЕ: объединяем данные проекта и файлы
          const updateData = {
            ...projectData,
            ...uploadedFiles
          };

          response = await this.editRequestToIssue([this.id, updateData]);

          // Обновляем локальный кэш
          if (this.index !== null) {
            this.requestsToIssue[this.index] = {
              ...response,
              additionalFile: uploadedFiles.additionalFile
            };
          }

          successMessage = "toasts.success.emissionEdit";
        }

        // 4. Успешное завершение
        this.showSuccess(this.$t(successMessage));
        this.$router.push({ name: "my projects" });
      }
      catch (error) {
        const errorMessage = error.response?.data?.message ||
                           error.response?.data?.detail ||
                           error.message;
        const toastKey = this.id === "new" ? "emissionSend" : "emissionEdit";
        this.showError(this.$t(`toasts.error.${toastKey}`) + `: ${errorMessage}`);
      }
      finally {
        this.loading = false;
      }
    },

    // НОВЫЙ МЕТОД: загрузка всех файлов
    async uploadAllFiles() {
      const fileParams = {};
      const third = this.thirdPartOfProject;

      if (!third) {
        return fileParams;
      }

      // Загрузка главного изображения
      if (third.image) {
        const upload = await this.uploadFile(third.image);
        if (upload) {
          fileParams.mainImage = `/api/uploaded_files/${upload.id}`;
        }
      }
      else if (third.oldImage) {
        fileParams.mainImage = third.oldImage;
      }

      // Загрузка иконки
      if (third.iconImage) {
        const upload = await this.uploadFile(third.iconImage);
        if (upload) {
          fileParams.icon = `/api/uploaded_files/${upload.id}`;
        }
      }
      else if (third.oldIconImage) {
        fileParams.icon = third.oldIconImage;
      }

      // Загрузка документов
      const docs = [];
      for (const doc of third.fileList || []) {
        if (doc.id) {
          await this.patchFile({
            id: doc.id,
            body: { embeddedCode: doc.embeddedCode }
          });
          docs.push(`/api/uploaded_files/${doc.id}`);
        }
        else {
          const upload = await this.uploadFile(doc);
          if (upload) {
            docs.push(`/api/uploaded_files/${upload.id}`);
          }
        }
      }

      if (docs.length > 0) {
        fileParams.additionalFile = docs;
      }

      return fileParams;
    },
    formatDate(date) {
      return new Date(date).toISOString();
    },

    async sendFile(file) {
      let upload = null;
      try {
        upload = await this.uploadFile(file);
      }
      catch (e) {
        this.showError(this.$t("toasts.error.emissionFile"));
      }
      return upload;
    },

    async sendDocs() {
      let ret = [];
      for (const doc of this.thirdPartOfProject.fileList) {
        if (doc.id) {
          await this.patchFile({
            id: doc.id,
            body: { embeddedCode: doc.embeddedCode }
          });
          ret.push(`/api/uploaded_files/${doc.id}`);
          continue;
        }

        const upload = await this.sendFile(doc);
        if (upload === null) {
          return [];
        }
        ret.push(`/api/uploaded_files/${upload.id}`);
      }
      return ret;
    },

    showSuccess(message) {
      this.$toast.success(message);
    },

    showError(message) {
      this.$toast.error(message);
    }
  }
};
</script>

<style></style>
